/**
 * ExpiringCertsPanel — accountants whose published cert is approaching
 * `not_after` (or has already expired).
 *
 * Drives the operator's proactive resign queue: each row gives them a
 * paste-and-run CLI command so they don't typo IDs into a terminal.
 *
 * Default window: 30 days. Operator can widen via the segmented control.
 */

import {
  Alert,
  Badge,
  Button,
  Code,
  CopyButton,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useCallback, useEffect, useState } from "react";

import { ApiError, api, type ExpiringCert } from "../lib/api";

const WINDOWS = ["7", "30", "90", "365"] as const;

export function ExpiringCertsPanel() {
  const [windowDays, setWindowDays] = useState<string>("30");
  const [items, setItems] = useState<ExpiringCert[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const resp = await api.expiringCerts(parseInt(windowDays, 10));
      setItems(resp.certs);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "fetch failed");
    }
  }, [windowDays]);

  useEffect(() => {
    refresh();
    const i = window.setInterval(refresh, 30_000);
    return () => window.clearInterval(i);
  }, [refresh]);

  if (error) return <Alert color="red">{error}</Alert>;
  if (items === null) return <Loader size="sm" />;

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Expiring certs</Title>
        <Group gap="sm">
          <Text c="dimmed" size="sm">
            Window:
          </Text>
          <SegmentedControl
            value={windowDays}
            onChange={setWindowDays}
            data={WINDOWS.map((w) => ({ value: w, label: `${w}d` }))}
          />
          <Button variant="light" onClick={refresh}>
            Refresh
          </Button>
        </Group>
      </Group>
      <Text c="dimmed">
        Certs that expire within the selected window (or are already
        expired). Already-expired rows are at the top — those are
        actively blocking client uploads.
      </Text>

      {items.length === 0 ? (
        <Paper p="xl" withBorder>
          <Text c="dimmed">
            Nothing expiring within {windowDays} days. 🎉
          </Text>
        </Paper>
      ) : (
        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Status</Table.Th>
                <Table.Th>Account</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Expires</Table.Th>
                <Table.Th>CLI</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((c) => {
                const cli = `dovach-op sign --account-id ${c.account_id.replace(/-/g, "")} --office-pk ${c.office_pk}`;
                return (
                  <Table.Tr key={c.account_id}>
                    <Table.Td>
                      <StatusBadge days={c.days_until_expiry} />
                    </Table.Td>
                    <Table.Td style={{ fontFamily: "monospace", fontSize: 12 }}>
                      {c.account_id.slice(0, 12)}…
                    </Table.Td>
                    <Table.Td>{c.email}</Table.Td>
                    <Table.Td>
                      <Code>{new Date(c.not_after).toLocaleDateString()}</Code>{" "}
                      <Text component="span" c="dimmed" size="sm">
                        ({formatDelta(c.days_until_expiry)})
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <CopyButton value={cli}>
                        {({ copied, copy }) => (
                          <Button
                            size="xs"
                            variant={copied ? "filled" : "light"}
                            color={copied ? "green" : "blue"}
                            onClick={copy}
                          >
                            {copied ? "Copied" : "Copy resign cmd"}
                          </Button>
                        )}
                      </CopyButton>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}

function StatusBadge({ days }: { days: number }) {
  if (days < 0) return <Badge color="red">EXPIRED</Badge>;
  if (days <= 7) return <Badge color="red">{days}d left</Badge>;
  if (days <= 30) return <Badge color="orange">{days}d left</Badge>;
  return <Badge color="yellow">{days}d left</Badge>;
}

function formatDelta(days: number): string {
  if (days < 0) return `${-days}d ago`;
  if (days === 0) return "today";
  return `in ${days}d`;
}
